// src/lib/leadSignals.js
// The signal vocabulary shared by the two leads surfaces: LeadsPage (the
// standalone view) and LeadsTab (rendered inside AccountDetailPage).
//
// WHY THIS MODULE EXISTS
// These three constants were duplicated verbatim in both components — B40 in
// TODO.md. Duplication of copy is not a style complaint here: the two files
// also each carried a `parseStructuredTasks`, and those DID silently diverge
// (LeadsPage's emits `_id` for its React keys, LeadsTab's does not). Nobody
// decided that; it just happened, and nothing caught it. The same was one edit
// away from happening to the pitches.
//
// PITCHES ARE FUNCTIONS OF THE COMPANY NAME
// `pitch` takes the tenant's company name and returns the sentence:
//
//   info.pitch(companyName)   ->  "Active modernization initiatives are
//                                  Acme's sweet spot. …"
//
// It is a function rather than a template string because this is one tenant's
// SALES COPY living in shared code. Hardcoding a name here shows a second
// tenant a competitor's pitch. The two entries that never name a company are
// functions too — a mixed shape invites a caller to render one of them raw,
// which React silently drops.
//
// The `services` lists are deliberately VENDOR-NEUTRAL. Two of them named
// Guidewire ("Guidewire Assessment", "Guidewire Implementation"), which asserts
// a partnership a tenant may not have — the same defect as the prompts, in UI
// copy. They now describe the work rather than the platform.
//
// They are still generic COPY rather than tenant data, which is a deliberate
// stopping point: an unset tenant would render empty service chips, and a blank
// UI list reads as a bug where an omitted prompt clause reads as silence. If
// these become per-tenant, the empty state needs a design, not just a fallback.

export const DIGITALME_ALIGNMENT = {
  'New Executive': {
    label: 'New Executive / Leadership Change',
    services: ['Executive Briefing', 'Core System Assessment', 'Digital Roadmap'],
    pitch: (co) => `New leaders often seek external validation of their technology stack and roadmap. An executive briefing positions ${co} as a trusted advisor from day one.`,
    icon: '👤'
  },
  'Tech Modernization': {
    label: 'Technology Modernization',
    services: ['Core System Implementation', 'Core System Modernization', 'Migration Services'],
    pitch: (co) => `Active modernization initiatives are ${co}'s sweet spot. We bring carrier-specific implementation expertise and proven accelerators.`,
    icon: '⚙️'
  },
  'Budget Allocated': {
    label: 'Budget Allocated',
    services: ['Project Kickoff', 'Staff Augmentation', 'Program Management'],
    pitch: () => 'Budget allocation signals urgency. Engage now to be positioned as the implementation partner before decisions are made.',
    icon: '💰'
  },
  'Market Expansion': {
    label: 'Market / Geographic Expansion',
    services: ['Product Configuration', 'Compliance Advisory', 'Rapid Deployment'],
    pitch: (co) => `Market expansion requires rapid deployment capability. ${co}'s proven methodologies compress timelines.`,
    icon: '🗺️'
  },
  'Financial Pressure': {
    label: 'Financial Pressure',
    services: ['Cost Optimization', 'Legacy Modernization', 'Efficiency Assessment'],
    pitch: (co) => `Financial pressure creates urgency around operational efficiency. Position ${co}'s modernization as cost reduction, not just capability upgrade.`,
    icon: '📉'
  },
  'Regulatory Change': {
    label: 'Regulatory / Compliance Change',
    services: ['Compliance Implementation', 'Regulatory Reporting', 'System Configuration'],
    pitch: (co) => `Regulatory deadlines create non-negotiable timelines. ${co}'s insurance domain expertise accelerates compliant implementations.`,
    icon: '⚖️'
  },
  'Conference/Trade Show': {
    label: 'Conference / Trade Show Presence',
    services: ['In-Person Engagement', 'Executive Networking', 'Demo Opportunities'],
    pitch: () => 'Conference attendance signals investment in peer learning and vendor relationships. Prime opportunity for face-to-face engagement.',
    icon: '🎪'
  }
};

export const TASK_TYPES      = ['Follow-up', 'Meeting Prep', 'Research', 'Proposal Work', 'Administrative'];
export const TASK_PRIORITIES = ['Do First', 'Do Today', 'This Week'];
